<template>
  <Form method="post" @submit="handleSubmit">
    <h1 class="text-center text-[2.5rem] leading-12 font-bold">
      {{ t('shared-link.welcome-back') }}
    </h1>
    <p class="mt-2 text-center font-semibold text-on-surface-secondary">
      {{ t('shared-link.sign-in-to-continue') }}
    </p>

    <div v-if="!isPasswordLoginDisabled" class="mt-6 flex flex-col gap-3">
      <TextInput
        v-model="email"
        :label="t('auth.email')"
        :placeholder="t('shared-link.email-placeholder')"
        :rules="[isRequired, isEmail]"
        data-testid="shared-link-email"
        @update:model-value="errorMessage = ''"
      />
      <PasswordInput
        v-model="password"
        :label="t('auth.password')"
        :placeholder="t('shared-link.password-placeholder')"
        :rules="[isRequired]"
        data-testid="shared-link-password"
        @update:model-value="errorMessage = ''"
      />
    </div>

    <Alert v-if="errorMessage" class="mt-4" :type="AlertType.ERROR" icon>
      {{ errorMessage }}
    </Alert>

    <Button
      v-if="!isPasswordLoginDisabled"
      :label="t('shared-link.sign-in')"
      class="mt-6 w-full"
      type="submit"
      data-testid="shared-link-sign-in"
    />

    <ExternalAuthProviders
      @error="errorMessage = t('auth.sso-error')"
      @link-accounts="onLinkAccounts"
    />

    <template v-if="!isPasswordLoginDisabled">
      <i18n-t
        keypath="shared-link.forgot-password"
        tag="p"
        class="mt-6 text-center text-on-surface-secondary"
      >
        <template #link>
          <NuxtLink to="/auth/reset-password" class="text-primary underline">
            {{ t('shared-link.reset-it') }}
          </NuxtLink>
        </template>
      </i18n-t>
      <i18n-t
        keypath="shared-link.no-access"
        tag="p"
        class="mt-3 text-center text-on-surface-secondary"
      >
        <template #link>
          <button
            type="button"
            class="text-primary underline"
            data-testid="shared-link-request-account"
            @click="emit('change-type', GET_ACCESS_STEP)"
          >
            {{ t('shared-link.request-account') }}
          </button>
        </template>
      </i18n-t>
    </template>
  </Form>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { Form } from 'vee-validate';
import { FirebaseError } from '@firebase/app';
import { signInWithEmailAndPassword } from '@firebase/auth';
import { AuthErrorCodes } from 'firebase/auth';
import { Alert, AlertType, Button } from '@exfluential/ui';
import {
  apiCheckEmail,
  apiResendInvitation,
  EmailStatus,
  SelfRegisterType,
} from '~/api/auth';
import { TextInput } from '~/components/inputs/text-input';
import { PasswordInput } from '~/components/inputs/password-input';
import { useFirebase } from '~/composables/use-firebase';
import { useLoader } from '~/composables/use-loader';
import { ExternalAuthProviders } from '~/pages/auth/_components/external-auth-providers';
import { useCompleteLogin } from '~/pages/auth/login/_composables/use-complete-login';
import {
  emailStatusLoginStepMap,
  GET_ACCESS_STEP,
  type LoginStep,
} from '~/pages/auth/login/login.constants';
import { useOrganizationStore } from '~/store/organization';
import { isEmailI18n, isRequiredI18n } from '~/utils/validators';

const email = defineModel<string>({ required: true });

const emit = defineEmits<{
  'change-type': [value: LoginStep];
}>();

const { t } = useI18n();
const { organization } = storeToRefs(useOrganizationStore());
const completeLogin = useCompleteLogin();

const isRequired = isRequiredI18n(t);
const isEmail = isEmailI18n(t);

const password = ref('');
const errorMessage = ref('');

const isPasswordLoginDisabled = computed(() =>
  Boolean(
    organization.value?.externalIdentityProviderData
      ?.passwordRegistrationDisabled,
  ),
);

const getErrorMessage = (err: unknown): string => {
  if (
    err instanceof FirebaseError &&
    err.code === AuthErrorCodes.INVALID_PASSWORD
  ) {
    return t('auth.incorrect-password');
  }
  if (err instanceof FirebaseError) {
    return t('auth.auth-failed');
  }
  return (
    (err as { data?: { message?: string } })?.data?.message ??
    t('auth.auth-failed')
  );
};

const handleSubmit = async () => {
  await useLoader({
    action: async () => {
      const { emailStatus } = await apiCheckEmail({ email: email.value });

      if (emailStatus !== EmailStatus.REGISTERED) {
        if (emailStatus === EmailStatus.PENDING) {
          await apiResendInvitation({ email: email.value });
        }
        emit('change-type', emailStatusLoginStepMap[emailStatus]);
        return;
      }

      if (!organization.value?.slug) throw new Error('Unknown organization');

      const { user } = await signInWithEmailAndPassword(
        useFirebase().auth,
        email.value,
        password.value,
      );

      await completeLogin({
        idToken: await user.getIdToken(true),
        loginMethod: 'password',
      });
    },
    onError: async (err: unknown) => {
      if (
        err instanceof FirebaseError &&
        err.code === AuthErrorCodes.USER_DISABLED
      ) {
        await apiResendInvitation({ email: email.value });
        emit('change-type', SelfRegisterType.RESEND_CONFIRMATION_INVITE);
        return;
      }
      errorMessage.value = getErrorMessage(err);
    },
  });
};

const onLinkAccounts = (emailFromProvider: string) => {
  if (emailFromProvider) {
    email.value = emailFromProvider;
  }
  emit('change-type', SelfRegisterType.ENTER_PASSWORD);
};
</script>
